import { useEffect, useState } from "react";
import { Alert, Button, FormGroup, Input } from "reactstrap";
import AppSpinner from "../../../elements/AppSpinner";
import { DrawerProps } from "../../Drawers/DrawerTypes";
import AppDrawer from "../../Drawers/AppDrawer";
import {
  CustomFlowChartBody,
  FlowChart,
  FlowTypes,
  HierarchicalFlowChartBody,
  useUpsertFlow,
} from "../../PageView/PageViewFlowChart/FlowChartDrawer";
import useRecord from "../../../api/api-hooks/records/useRecord";
import AppFieldCard from "../../../elements/AppFieldCard";
import PageAPI from "../../../api/api/PageAPI";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { useQueryClient } from "@tanstack/react-query";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import TenantAPI from "../../../api/api/TenantAPI";
import { FlowChartConfigI } from "../../../types/PageInterfaces";
import useView from "../../../api/api-hooks/views/useView";

interface Props extends DrawerProps {
  setSettings: any;
  section: any;
  updateWidget: any;
  isUpdating: boolean;
  pageID: string;
  viewID: string;
  insightsModule?: string;
  isSubview?: boolean;
}

export interface FlowWidgetI {
  id: string;
  Type: "FlowChart";
  Name: string;
  Description: string;
  RecordID: string | undefined; // The record ID of the FlowChart config.
  ModuleAPIName?: string;
  FlowChartConfig?: FlowChartConfigI; // ONLY exists when ModuleAPIName exists.
}

export default function FlowWidgetSettings({
  open,
  onClose,
  setSettings,
  section,
  updateWidget,
  isUpdating,
  pageID,
  viewID,
  insightsModule,
  isSubview,
}: Props) {
  const isExisting = Boolean(section?.RecordID);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const client = useQueryClient();

  // There might not be a Flow Chart Record yet
  // For example after initial creation of the widget
  const { record: flowRecord } = useRecord({
    moduleAPIName: "Flows",
    recordID: section?.RecordID,
    enabled: !isSubview,
  });
  const { view } = useView({ pageID, viewID });
  const { topLevelRecords } = useUpsertFlow({
    view: view!,
    pageID: pageID,
  });

  const fType = flowRecord?.FlowType || "Hierarchy";
  const flowChartConfig =
    flowRecord?.FlowChartConfig || section?.FlowChartConfig;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [widget, setWidget] = useState<FlowWidgetI>(section);

  const [flowType, setFlowType] = useState(fType);
  const [flowData, setFlowData] = useState<Partial<FlowChart>>({
    FlowChartConfig: {
      Modules: insightsModule
        ? {
            [insightsModule]: {
              Fields: [],
              ParentField: "",
            },
          }
        : {},
    },
  });

  // Ensure configuration state is synced with the Flow Chart Record
  useEffect(() => {
    if (!flowChartConfig) return;

    setFlowType(fType);
    setFlowData({ FlowChartConfig: flowChartConfig });
  }, [fType, flowChartConfig]);

  useEffect(() => {
    setWidget(structuredClone(section));
  }, [section]);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      headerTitle="Flow Chart Settings"
      headerOptions={
        <Button
          color="primary"
          size="sm"
          onClick={handleSave}
          disabled={
            isUpdating ||
            submitting ||
            !widget?.Name ||
            !insightsModule ||
            !flowData?.FlowChartConfig?.Modules[insightsModule]?.ParentField
          }
          style={{ minWidth: 80 }}
        >
          {isUpdating || submitting ? (
            <AppSpinner size="sm" color="white" />
          ) : (
            "Save"
          )}
        </Button>
      }
      bodyCSS="pt-3"
      body={
        <>
          {error && (
            <Alert color="danger" className="py-2">
              {error}
            </Alert>
          )}
          <FormGroup>
            <h4>Title</h4>
            <Input
              bsSize="sm"
              value={widget?.Name}
              onChange={(e) =>
                setWidget((prev) => ({ ...prev, Name: e.target.value }))
              }
            />
          </FormGroup>
          <FormGroup>
            <h4>Description</h4>
            <Input
              bsSize="sm"
              type="textarea"
              value={widget?.Description}
              onChange={(e) =>
                setWidget((prev) => ({ ...prev, Description: e.target.value }))
              }
            />
          </FormGroup>

          <h4 className="pt-1">Flow Type</h4>
          {FlowTypes.map((flowTypeData) => {
            if (flowTypeData.value === "Custom") return null;

            return (
              <AppFieldCard
                full
                key={flowTypeData.value}
                // @ts-ignore
                onClick={() => setFlowType(flowTypeData.value)}
                isSelected={flowType === flowTypeData.value}
                icon={flowTypeData.icon}
                name={flowTypeData.label}
                description={flowTypeData.description}
                css="mb-2"
                innerCSS=""
                disabled={false}
              />
            );
          })}
          <div className="mb-4"></div>

          {flowType === "Custom" && (
            <CustomFlowChartBody
              flowData={flowData}
              setFlowData={setFlowData}
            />
          )}
          {flowType === "Hierarchy" && (
            <HierarchicalFlowChartBody
              flowData={flowData}
              setFlowData={setFlowData}
              isExisting={!!section?.RecordID}
              moduleSelectionDisabled
              topLevelRecords={topLevelRecords}
            />
          )}
        </>
      }
    />
  );

  function handleSave() {
    // If a Subview, update the Widget's FlowChartConfig ONLY.
    // There is NO flow record for SubView widgets. (hundreds of thousands of records would be needed, we use on demand hierarchy flow generation instead for them.)
    if (isSubview) {
      const w = {
        ...widget,
        FlowChartConfig: flowData.FlowChartConfig,
        ModuleAPIName: insightsModule,
      };

      return updateWidget(w, () => {
        onClose();
        setWidget(w);
      });
    }

    if (isExisting) {
      updateWidgetFlowRecord();
    } else {
      createWidgetFlowRecord();
    }
  }

  async function updateWidgetFlowRecord() {
    setSubmitting(true);
    setError("");

    try {
      // If not a Subview, Update Flow Record
      const data = await TenantAPI.modifyRecord("Flows", section.RecordID, {
        payload: {
          FlowChartConfig: flowData.FlowChartConfig,
          ...(!flowData?.ViewID && { ViewID: viewID }),
        },
      });

      const flow = data.data.data.data;

      if (!flow) throw "Flow record not updated";

      client.setQueryData([tenantID, "record", "Flows", flow._id], flow);

      if (
        widget?.Name !== section?.Name ||
        widget?.Description !== section?.Description
      ) {
        updateWidget(widget, onClose);
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update flow chart record."));
    } finally {
      setSubmitting(false);
    }
  }

  async function createWidgetFlowRecord() {
    setSubmitting(true);
    setError("");

    try {
      //  1 - Create Flow Record
      const data = await PageAPI.generateFlowChart(
        workspaceID,
        pageID,
        viewID,
        { ...flowData, ViewID: viewID, FlowType: flowType }
      );

      const flow = data.data.data.data;

      if (!flow) throw "Flow record not created";

      client.setQueryData([tenantID, "record", "Flows", flow._id], flow);

      // 2 - Update Widget with Flow Record ID.
      updateWidget({ ...widget, RecordID: flow._id }, () => {
        onClose();
        setWidget((prev) => ({ ...prev, RecordID: flow._id }));
      });
    } catch (err) {
      setError(getErrorMessage(err, "Failed to create flow chart record."));
    } finally {
      setSubmitting(false);
    }
  }
}
